import { useState, useMemo } from 'react';

// Combines client-side sorting + pagination for a data array. Resets to
// page 1 whenever the sort changes or the underlying data set shrinks below
// the current page.
export const useTableControls = (data, pageSize = 20) => {
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);

    const sorted = useMemo(() => {
        if (!sortField) return data;
        const copy = [...data];
        copy.sort((a, b) => {
            let av = a[sortField];
            let bv = b[sortField];
            if (av === undefined || av === null) av = '';
            if (bv === undefined || bv === null) bv = '';
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av < bv) return sortDirection === 'asc' ? -1 : 1;
            if (av > bv) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return copy;
    }, [data, sortField, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedData = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return sorted.slice(start, start + pageSize);
    }, [sorted, safePage, pageSize]);

    const requestSort = (field) => {
        if (sortField === field) {
            setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };

    return {
        paginatedData,
        sortField,
        sortDirection,
        requestSort,
        currentPage: safePage,
        totalPages,
        setPage: setCurrentPage,
        totalItems: sorted.length
    };
};
